<script setup lang="ts">
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * ⚠️  DEPRECATED — DO NOT USE — SCHEDULED FOR DELETION
   * ═══════════════════════════════════════════════════════════════════════
   *
   * This component is deprecated. Use OptimisedImage instead:
   *
   *   import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'
   *
   * Migration guide: .cursor/rules/image-components-guide.mdc
   *
   * Key differences:
   * - width/height in OptimisedImage controls BOTH optimization AND display
   * - Dimension classes in imgClass are auto-filtered in OptimisedImage
   * - w-full h-full auto-added when gradient is enabled
   *
   * ═══════════════════════════════════════════════════════════════════════
   */

  import { computed, defineAsyncComponent, onMounted, ref, watch, type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'

  import { useImageAspectRatio, type UseImageAspectRatioOptions } from '../composables/useImageAspectRatio'

  import { generateDRMImageUrl } from '../utils/image.utils'
  import { isValidGalleryLicenseId } from '../utils/media-helpers'

  // Lazy load MediaGalleryModal - only loads when preview is clicked
  const MediaGalleryModal = defineAsyncComponent(() => import('./MediaGalleryModal.vue'))

  const props = defineProps({
    licenseId: {
      type: String,
      default: '',
    },
    src: {
      type: String,
      default: '',
    },
    source: {
      type: String as PropType<'communities' | 'courses' | 'clientportal' | string>,
      default: 'communities',
    },
    alt: {
      type: String,
      default: '',
    },
    fallbackAspect: {
      type: String,
      default: 'aspect-square',
    },
    contain: {
      type: Boolean,
      default: false,
    },
    imgClass: {
      type: String,
      default: '',
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    useGradientBackground: {
      type: Boolean,
      default: true,
    },
    forceGradient: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: Number,
      default: undefined,
    },
    maxHeight: {
      type: Number,
      default: undefined,
    },
    width: {
      type: Number,
      default: undefined,
    },
    height: {
      type: Number,
      default: undefined,
    },
    quality: {
      type: Number,
      default: 90,
    },
    options: {
      type: Object as PropType<UseImageAspectRatioOptions>,
      default: () => ({}),
    },
    preview: {
      type: Boolean,
      default: false,
    },
    useRawUrl: {
      type: Boolean,
      default: false,
    },
    // NuxtImg / native img element props
    loading: {
      type: String as PropType<'lazy' | 'eager'>,
      default: 'lazy',
    },
    decoding: {
      type: String as PropType<'async' | 'auto' | 'sync'>,
      default: 'async',
    },
    crossorigin: {
      type: String as PropType<'anonymous' | 'use-credentials'>,
      default: 'anonymous',
    },
    fetchpriority: {
      type: String as PropType<'high' | 'low' | 'auto'>,
      default: 'auto',
    },
    // Additional NuxtImg props (no duplicates with existing SmartImage props)
    format: {
      type: String,
      default: 'webp',
    },
    fit: {
      type: String as PropType<'cover' | 'contain' | 'fill' | 'inside' | 'outside'>,
      default: undefined,
    },
    preload: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: [String, Boolean, Number],
      default: undefined,
    },
    sizes: {
      type: String,
      default: undefined,
    },
    densities: {
      type: String,
      default: undefined,
    },
  })

  const emit = defineEmits<{
    error: [event: Event]
    load: []
  }>()

  const { t } = useI18n()

  // Smart URL resolution: handles licenseId, regular URLs, and blob URLs
  const optimizedImageUrl = computed(() => {
    if (props.useRawUrl) {
      return props.src
    }
    // Priority 1: If src is a blob URL (during upload), use it directly
    if (props.src?.startsWith('blob:')) {
      return props.src
    }

    // Priority 2: If licenseId looks like a URL, use it as src instead
    const actualLicenseId = isValidGalleryLicenseId(props.licenseId) ? props.licenseId : undefined
    const actualSrc = actualLicenseId ? props.src : props.licenseId || props.src

    // Priority 3: Generate optimized DRM URL
    return generateDRMImageUrl({
      licenseId: actualLicenseId,
      src: actualSrc,
      source: props.source,
      width: props.width,
      height: props.height,
      quality: props.quality,
      format: (props.format as 'jpg' | 'png' | 'webp' | 'avif' | 'gif' | 'tiff' | 'heif' | 'jp2' | 'jxl') || 'webp',
      resize: props.contain ? 'fit' : 'fill',
    })
  })

  const { isLoaded, aspectClass, objectFitClass, measure, error, needsGradientBackground } = useImageAspectRatio({
    preferContain: props.contain,
    useGradientBackground: props.useGradientBackground ?? true, // Enable by default
    ...(props.options ?? {}),
  })

  const isReady = computed(() => isLoaded.value || Boolean(error.value))

  const hasError = ref(false)

  const resolvedAspect = computed(() => {
    // When forcing gradient (in grids/collages), don't apply any aspect ratio
    if (props.forceGradient) {
      return ''
    }

    // When gradient background is enabled and we want object-contain,
    // use the fallback aspect to maintain a fixed container size
    // This creates the empty space that needs the gradient fill
    const shouldUseFallback = props.useGradientBackground && props.contain === false && isReady.value
    const aspect = shouldUseFallback
      ? props.fallbackAspect || 'aspect-video'
      : isReady.value
        ? aspectClass.value
        : props.fallbackAspect || 'aspect-square'

    return aspect
  })

  const fitClass = computed(() => objectFitClass.value)

  // Loading state for shimmer
  // const isLoading = ref(true)

  // Container style with dimensions
  const containerStyle = computed(() => {
    const style: Record<string, string> = {}

    // NOTE: width/height props are ONLY for URL generation (wdt/hgt parameters)
    // They should NOT affect container styling to avoid layout shifts
    // Container size is controlled by CSS classes from parent (w-full, h-full, etc.)

    // Apply max dimensions only if explicitly provided
    if (props.maxWidth !== undefined) {
      style.maxWidth = `${props.maxWidth}px`
    }
    if (props.maxHeight !== undefined) {
      style.maxHeight = `${props.maxHeight}px`
    }

    // If no explicit dimensions provided, don't constrain - let parent control size
    return style
  })

  // Media gallery state
  const showGallery = ref(false)

  const galleryAttachments = computed(() => {
    // Use smart detection for gallery too
    const validLicenseId = isValidGalleryLicenseId(props.licenseId) ? props.licenseId : undefined

    return [
      {
        type: 'image' as const,
        url: optimizedImageUrl.value,
        id: validLicenseId || props.src,
        licenseId: validLicenseId,
        alt: props.alt,
      },
    ]
  })

  const handleImageClick = () => {
    if (props.preview) {
      showGallery.value = true
    }
  }

  const closeGallery = () => {
    showGallery.value = false
  }

  onMounted(() => {
    // Eagerly measure on mount so the wrapper snaps to the correct ratio quickly
    if (optimizedImageUrl.value) measure(optimizedImageUrl.value)
  })

  function onLoad() {
    if (!isLoaded.value) measure(optimizedImageUrl.value)
    hasError.value = false
    emit('load')
  }

  function onError(event: Event | string) {
    hasError.value = true
    // NuxtImg can emit either an Event or a string error message
    // Normalize to Event for consistent consumer API
    const errorEvent = typeof event === 'string' ? new Event('error') : event
    emit('error', errorEvent)
  }

  // Reset error state when image URL changes
  watch(
    () => optimizedImageUrl.value,
    (newUrl, oldUrl) => {
      if (newUrl !== oldUrl && newUrl) {
        hasError.value = false
      }
    }
  )
</script>

<template>
  <div class="h-full w-full">
    <div
      :id="createElementId('thumbnail', 'image', 'container')"
      :class="['relative h-full w-full overflow-hidden', resolvedAspect, rounded ? 'rounded' : '', preview ? 'cursor-pointer' : '']"
      :style="containerStyle"
      @click="handleImageClick"
    >
      <!-- Blurred background image for empty spaces -->
      <NuxtImg
        v-if="optimizedImageUrl && (forceGradient || (isReady && needsGradientBackground))"
        :id="createElementId('thumbnail', 'image', 'background')"
        :key="`bg-${optimizedImageUrl}`"
        :src="optimizedImageUrl"
        :alt="`Background for ${alt || 'image'}`"
        :loading="loading"
        :decoding="decoding"
        :fetchpriority="fetchpriority"
        class="absolute inset-0 h-full w-full scale-110 object-cover opacity-100 blur-md"
      />

      <!-- Main image -->
      <NuxtImg
        v-if="optimizedImageUrl && !hasError"
        :id="createElementId('thumbnail', 'image', 'main')"
        :key="optimizedImageUrl"
        :src="optimizedImageUrl"
        :alt="alt || ''"
        :loading="loading"
        :decoding="decoding"
        :fetchpriority="fetchpriority"
        :class="['relative z-10 h-full w-full', fitClass, imgClass]"
        @load="onLoad"
        @error="onError"
      />

      <!-- Error state - broken image icon -->
      <div v-if="hasError" class="absolute inset-0 z-20 flex flex-col items-center justify-center bg-secondary">
        <svg
          class="h-12 w-12 text-[var(--cp-font-400)]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span class="mt-2 text-xs text-muted">{{ t('media.failedToLoad') }}</span>
      </div>

      <!-- Loading shimmer overlay -->
      <!-- <Transition name="fade">
      <div
        v-if="isLoading"
        class="absolute inset-0 shimmer-bg z-30"
      />
    </Transition> -->
    </div>

    <!-- Media Gallery Modal -->
    <MediaGalleryModal
      :show="showGallery"
      :attachments="galleryAttachments"
      :initial-index="0"
      :show-thumbnails="false"
      :allow-navigation="false"
      :use-raw-url="useRawUrl"
      @close="closeGallery"
    />
  </div>
</template>

<style scoped>
  /* Shimmer animation */
  .shimmer-bg {
    background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  /* Fade transition */
  .fade-enter-active {
    transition: opacity 0.4s ease-in;
  }

  .fade-leave-active {
    transition: opacity 0.3s ease-out;
  }

  .fade-enter-from {
    opacity: 0;
  }

  .fade-leave-to {
    opacity: 0;
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
  }
</style>
